import React, { useState, useEffect } from 'react';
import { baseUrl } from '../../shared';
import dayjs from 'dayjs';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { EquipmentSelector, MobileTransducerPicker, applyOffset } from './CalibrationShared';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const DEFAULT_ROWS = [1000,2000,3000,4000,5000,6000,7000,8000,9000,10000].map(psi => ({
  psi, p1: '', p2: '', p3: '',
}));

const EMPTY_FORM = {
  technician: '', calibrationDate: '', calDueDate: '',
  temperature: '', humidity: '', passFailLimitPct: 4, comments: '',
  targetTorqueFactor: '',
};

function calcHydRow(row, targetFactor, limitPct, offsetNm) {
  const correction = parseFloat(offsetNm) || 0;
  const rawVals = [row.p1, row.p2, row.p3].map(v => parseFloat(v)).filter(v => !isNaN(v));
  if (rawVals.length === 0 || !targetFactor) return null;
  const correctedVals = rawVals.map(v => v + correction);
  const avg = correctedVals.reduce((a, b) => a + b, 0) / correctedVals.length;
  const rawAvg = rawVals.reduce((a, b) => a + b, 0) / rawVals.length;
  const torqueFactor = avg / row.psi;
  const deviationPct = ((torqueFactor - targetFactor) / targetFactor) * 100;
  const pass = Math.abs(deviationPct) <= limitPct;
  const avgFtLb = avg * 0.7376;
  return { avg, rawAvg, correction, torqueFactor, deviationPct, pass, avgFtLb };
}

export default function HydPressureCalForm({ open, onClose, job, cal, canManage, onSaved }) {
  const readOnly = !canManage;
  const [form, setForm] = useState(EMPTY_FORM);
  const [rows, setRows] = useState(DEFAULT_ROWS.map(r => ({ ...r })));
  const [equipmentUsed, setEquipmentUsed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileTransducerId, setMobileTransducerId] = useState(null);
  const [mobileTransducer, setMobileTransducer] = useState(null);

  const mobileOffsets = isMobile && mobileTransducer && mobileTransducer.latestCorrectionData
    ? mobileTransducer.latestCorrectionData
    : null;

  useEffect(() => {
    if (!open) return;
    if (cal) {
      setForm({
        technician: cal.technician || '',
        calibrationDate: cal.calibrationDate ? dayjs(cal.calibrationDate).format('YYYY-MM-DD') : '',
        calDueDate: cal.calDueDate ? dayjs(cal.calDueDate).format('YYYY-MM-DD') : '',
        temperature: cal.temperature != null ? String(cal.temperature) : '',
        humidity: cal.humidity != null ? String(cal.humidity) : '',
        passFailLimitPct: cal.passFailLimitPct ?? 4,
        comments: cal.comments || '',
        targetTorqueFactor: cal.rawData?.targetTorqueFactor || '',
      });
      setRows(cal.rawData?.rows?.length ? cal.rawData.rows : DEFAULT_ROWS.map(r => ({ ...r })));
      setEquipmentUsed(cal.rawData?.equipmentSnapshot || []);
      setIsMobile(cal.subtype === 'MOBILE');
      setMobileTransducerId(cal.rawData?.mobileTransducerId || null);
      setMobileTransducer(null);
    } else {
      setForm({ ...EMPTY_FORM });
      setRows(DEFAULT_ROWS.map(r => ({ ...r })));
      setEquipmentUsed([]);
      setIsMobile(false);
      setMobileTransducerId(null);
      setMobileTransducer(null);
    }
  }, [open, cal]);

  useEffect(() => {
    if (form.calibrationDate && !cal?.calDueDate) {
      setForm(f => ({ ...f, calDueDate: dayjs(form.calibrationDate).add(12, 'month').format('YYYY-MM-DD') }));
    }
  }, [form.calibrationDate]);

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const setCell = (i, f) => e => setRows(r => r.map((row, idx) => idx === i ? { ...row, [f]: e.target.value } : row));

  const limitPct = parseFloat(form.passFailLimitPct) || 4;
  const targetFactor = parseFloat(form.targetTorqueFactor) || null;
  const rowCalcs = rows.map(r => {
    const offsetNm = mobileOffsets ? applyOffset(r.psi, mobileOffsets) : 0;
    return calcHydRow(r, targetFactor, limitPct, offsetNm);
  });
  const allCalc = rowCalcs.every(r => r !== null);
  const overallResult = allCalc ? (rowCalcs.every(r => r.pass) ? 'PASS' : 'FAIL') : null;

  const avgTorqueFactor = allCalc
    ? (rowCalcs.reduce((sum, r) => sum + r.torqueFactor, 0) / rowCalcs.length).toFixed(4)
    : null;

  const handleSave = async () => {
    if (equipmentUsed.length === 0) {
      alert('Please select the test equipment used before saving.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        type: 'HYD_PRESSURE',
        subtype: isMobile ? 'MOBILE' : null,
        overallResult: overallResult || null,
        rawData: {
          rows,
          targetTorqueFactor: form.targetTorqueFactor,
          equipmentSnapshot: equipmentUsed,
          ...(isMobile && mobileTransducerId ? {
            mobileTransducerId,
            mobileTransducerName: mobileTransducer ? mobileTransducer.name + (mobileTransducer.serialNo ? ' S/N ' + mobileTransducer.serialNo : '') : '',
          } : {}),
        },
      };
      const url = cal
        ? baseUrl + 'workshop/jobs/' + job.id + '/calibrations/' + cal.id
        : baseUrl + 'workshop/jobs/' + job.id + '/calibrations';
      const res = await fetch(url, {
        method: cal ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) onSaved();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
            {cal ? `Hydraulic Pressure/Torque — ${cal.certNo || 'Draft'}` : 'New Hydraulic Pressure/Torque Calibration'}
          </Typography>
          {overallResult && (
            <Chip label={overallResult} sx={{ bgcolor: overallResult === 'PASS' ? 'var(--pass-bg)' : 'var(--fail-bg)', color: overallResult === 'PASS' ? 'var(--pass)' : 'var(--fail)', fontWeight: 700, fontSize: 14 }} />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}><Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Calibration Details</Typography><Divider sx={{ mt: 0.5, mb: 1.5 }} /></Grid>

          <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Technician" value={form.technician} onChange={set('technician')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Calibration Date" type="date" value={form.calibrationDate} onChange={set('calibrationDate')} InputLabelProps={{ shrink: true }} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Cal Due Date" type="date" value={form.calDueDate} onChange={set('calDueDate')} InputLabelProps={{ shrink: true }} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Pass/Fail Limit %" type="number" value={form.passFailLimitPct} onChange={set('passFailLimitPct')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Temperature (°C)" type="number" value={form.temperature} onChange={set('temperature')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Humidity (%)" type="number" value={form.humidity} onChange={set('humidity')} InputProps={{ readOnly }} /></Grid>
          <Grid item xs={12} sm={3}>
            <TextField size="small" fullWidth label="Target Torque Factor (NM/PSI)" type="number" value={form.targetTorqueFactor} onChange={set('targetTorqueFactor')} InputProps={{ readOnly }}
              helperText={avgTorqueFactor ? `Avg from data: ${avgTorqueFactor} NM/PSI` : 'Enter to enable pass/fail'} />
          </Grid>
          <Grid item xs={12} sm={5}><TextField size="small" fullWidth label="Comments" value={form.comments} onChange={set('comments')} InputProps={{ readOnly }} /></Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Test Equipment Used</Typography>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            <EquipmentSelector selected={equipmentUsed} onChange={setEquipmentUsed} readOnly={readOnly} />
          </Grid>

          {/* Mobile calibration mode */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Calibration Method</Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {!readOnly && (
                <FormControlLabel
                  control={<Checkbox checked={isMobile} onChange={e => setIsMobile(e.target.checked)} size="small" />}
                  label={<Typography variant="body2">Mobile calibration (portable transducer — offsets applied per PSI point)</Typography>}
                />
              )}
              {readOnly && (
                <Typography variant="body2" color={isMobile ? 'primary' : 'text.secondary'}>
                  {isMobile ? 'Mobile calibration — portable transducer used' : 'Bench calibration'}
                </Typography>
              )}
              {isMobile && (
                <Box sx={{ ml: readOnly ? 0 : 3.5, maxWidth: 480 }}>
                  {readOnly
                    ? <MobileTransducerPicker value={mobileTransducerId} onChange={() => {}} readOnly />
                    : <MobileTransducerPicker
                        value={mobileTransducerId}
                        onChange={(id, t) => { setMobileTransducerId(id); setMobileTransducer(t); }}
                        readOnly={false}
                      />
                  }
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Pressure / Torque Measurement Table</Typography>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: mobileOffsets ? 820 : 700 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'var(--surface-2)' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Pressure (PSI)</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Pass 1 (NM)</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Pass 2 (NM)</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Pass 3 (NM)</TableCell>
                    {mobileOffsets && <TableCell sx={{ fontWeight: 600, fontSize: 11, color: 'text.secondary' }}>Raw Avg</TableCell>}
                    <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Avg NM{mobileOffsets ? ' (Corr)' : ''}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Avg FT/LB</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>TF (NM/PSI)</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Dev%</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>P/F</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, i) => {
                    const c = rowCalcs[i];
                    return (
                      <TableRow key={i} sx={{ bgcolor: c ? (c.pass ? 'var(--pass-bg)' : 'var(--fail-bg)') : undefined }}>
                        <TableCell sx={{ fontWeight: 600 }}>{row.psi}</TableCell>
                        {['p1', 'p2', 'p3'].map(p => (
                          <TableCell key={p}>
                            {readOnly ? (row[p] || '—') : (
                              <TextField size="small" variant="standard" type="number" value={row[p]} onChange={setCell(i, p)} sx={{ width: 75 }} inputProps={{ style: { fontSize: 13 } }} />
                            )}
                          </TableCell>
                        ))}
                        {mobileOffsets && (
                          <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{c ? c.rawAvg.toFixed(2) : '—'}</TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 600 }}>
                          {c ? c.avg.toFixed(2) : '—'}
                          {mobileOffsets && c && c.correction !== 0 && (
                            <Typography variant="caption" color="var(--info)" sx={{ display: 'block', fontSize: 10 }}>+{c.correction.toFixed(2)}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{c ? c.avgFtLb.toFixed(2) : '—'}</TableCell>
                        <TableCell>{c ? c.torqueFactor.toFixed(4) : '—'}</TableCell>
                        <TableCell sx={{ color: c && !c.pass ? 'error.main' : undefined, fontSize: 12 }}>
                          {c ? `${c.deviationPct >= 0 ? '+' : ''}${c.deviationPct.toFixed(2)}%` : '—'}
                        </TableCell>
                        <TableCell>
                          {c && <Chip label={c.pass ? 'PASS' : 'FAIL'} size="small" sx={{ bgcolor: c.pass ? 'var(--pass-bg)' : 'var(--fail-bg)', color: c.pass ? 'var(--pass)' : 'var(--fail)', fontWeight: 700, fontSize: 11 }} />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Grid>

          {overallResult && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1, p: 2, borderRadius: 1, textAlign: 'center', bgcolor: overallResult === 'PASS' ? 'var(--pass-bg)' : 'var(--fail-bg)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: overallResult === 'PASS' ? 'var(--pass)' : 'var(--fail)' }}>OVERALL RESULT: {overallResult}</Typography>
                <Typography variant="caption" color="text.secondary">Average Torque Factor: {avgTorqueFactor} NM/PSI — ±{limitPct}% tolerance</Typography>
              </Box>
            </Grid>
          )}
          {!overallResult && !form.targetTorqueFactor && (
            <Grid item xs={12}><Alert severity="info">Enter a target torque factor to enable pass/fail calculation.</Alert></Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        {canManage && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : cal ? 'Save Changes' : 'Save Calibration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
